<script setup lang="ts">
/* see docs/EXTRACTED_COMMENTS.md#c040 */
import { computed, onBeforeUnmount, onMounted, ref, watch, type CSSProperties } from "vue"
import type EditorJS from "@editorjs/editorjs"
import type { OutputData } from "@editorjs/editorjs"
import { strToU8, strFromU8, zlibSync, decompressSync } from "fflate"

import { Component } from "../types.ts"
import useStore from "../store.ts"
import {
  DEFAULT_FONT_SIZE,
  FONT_SIZE_OPTIONS,
  makeEmptyDefaults,
  type BackgroundOption,
  type EditorDefaults,
  type FontOption,
  type ForegroundOption,
} from "./editor/types.ts"
import { toRgb } from "./editor/colorUtils.ts"
import { SpreadsheetTableTool } from "./editor/SpreadsheetTableTool.ts"
import { EditorDefaultsTune } from "./editor/EditorDefaultsTune.ts"
import { ForegroundPickerTool } from "./editor/ForegroundPickerTool.ts"
import { BackgroundPickerTool } from "./editor/BackgroundPickerTool.ts"
import { FontPickerTool } from "./editor/FontPickerTool.ts"
import { FontSizeTool } from "./editor/FontSizeTool.ts"

const BG_TOKEN_OPEN = "[[WDC_BG:"
const BG_TOKEN_CLOSE = "[[/WDC_BG]]"

const { item } = defineProps<{
  item: Component
}>()

const holderRef = ref<HTMLDivElement | null>(null)
const styleRef = ref<HTMLStyleElement | null>(null)
const boxRef = ref<HTMLDivElement | null>(null)
const scale = ref(1)

const holderId = `editor_${item.props.id.replace(/\./g, "_")}`

// see docs/EXTRACTED_COMMENTS.md#c335
const outputOnly = item.editor_props?.output_only === true
const readOnly = outputOnly
const displayWidth = item.editor_props?.display_width ?? 0
const displayHeight = item.editor_props?.display_height ?? 0
const isScaled = displayWidth > 0 && displayHeight > 0

const canvasStyle = computed<CSSProperties>(() => {
  if (!isScaled) {
    return { position: "absolute", top: 0, left: 0, width: "100%" }
  }
  return {
    position: "absolute",
    top: 0,
    left: 0,
    width: `${displayWidth}px`,
    height: `${displayHeight}px`,
    transformOrigin: "top left",
    transform: `scale(${scale.value})`,
  }
})

// see docs/EXTRACTED_COMMENTS.md#c336
let toolbarOffset = { x: 0, y: 0 }
let toolbarObserver: MutationObserver | null = null
let toolbarMove: ((event: PointerEvent) => void) | null = null

// see docs/EXTRACTED_COMMENTS.md#c337
const MIN_GRID_HEIGHT = 64
let gridResizeObserver: ResizeObserver | null = null

function relayoutGrids(): void {
  const holder = holderRef.value
  const box = boxRef.value
  if (!holder || !box) return
  const redactor = holder.querySelector<HTMLElement>(".codex-editor__redactor")
  const mains = holder.querySelectorAll<HTMLElement>(".wdc-grid__main")
  if (!redactor || !mains.length) return

  // see docs/EXTRACTED_COMMENTS.md#c338
  const available = isScaled ? displayHeight : box.clientHeight
  if (available <= 0) return

  mains.forEach(main => {
    // see docs/EXTRACTED_COMMENTS.md#c339
    const other = redactor.scrollHeight - main.offsetHeight
    const cap = Math.max(MIN_GRID_HEIGHT, available - other)
    const next = `${cap}px`
    if (main.style.maxHeight !== next) main.style.maxHeight = next
  })
}

function computeScale(): number {
  const box = boxRef.value
  if (!isScaled || !box) return 1
  const bw = box.clientWidth
  const bh = box.clientHeight
  if (bw <= 0 || bh <= 0) return 1
  const s = Math.min(bw / displayWidth, bh / displayHeight)
  return Number.isFinite(s) && s > 0 ? s : 1
}

function updateScale(): void {
  if (!isScaled) return
  scale.value = computeScale()
}

function setupGridResize(): void {
  const holder = holderRef.value
  const box = boxRef.value
  if (!holder || !box || typeof ResizeObserver === "undefined") return
  gridResizeObserver = new ResizeObserver(() => {
    updateScale()
    relayoutGrids()
  })
  gridResizeObserver.observe(box)
  const redactor = holder.querySelector<HTMLElement>(".codex-editor__redactor")
  if (redactor) gridResizeObserver.observe(redactor)
  updateScale()
  relayoutGrids()
}

function teardownGridResize(): void {
  gridResizeObserver?.disconnect()
  gridResizeObserver = null
}

function applyToolbarOffset(toolbar: HTMLElement): void {
  toolbar.style.transform = `translate(${toolbarOffset.x}px, ${toolbarOffset.y}px)`
}

function attachToolbarGrip(toolbar: HTMLElement): void {
  if (toolbar.querySelector(".wdc-tb-grip")) return

  const grip = document.createElement("button")
  grip.type = "button"
  grip.className = "wdc-tb-grip"
  grip.title = "Drag toolbar"
  grip.innerHTML = `<svg width="8" height="16" viewBox="0 0 8 16" aria-hidden="true">
    <circle cx="2" cy="3" r="1.2"/><circle cx="6" cy="3" r="1.2"/>
    <circle cx="2" cy="8" r="1.2"/><circle cx="6" cy="8" r="1.2"/>
    <circle cx="2" cy="13" r="1.2"/><circle cx="6" cy="13" r="1.2"/>
  </svg>`
  toolbar.insertBefore(grip, toolbar.firstChild)
  applyToolbarOffset(toolbar)

  let start = { px: 0, py: 0, ox: 0, oy: 0 }
  const end = () => {
    if (toolbarMove) window.removeEventListener("pointermove", toolbarMove)
    window.removeEventListener("pointerup", end)
    toolbarMove = null
  }
  grip.addEventListener("pointerdown", event => {
    event.preventDefault()
    event.stopPropagation()
    start = {
      px: event.clientX,
      py: event.clientY,
      ox: toolbarOffset.x,
      oy: toolbarOffset.y,
    }
    toolbarMove = move => {
      toolbarOffset = {
        x: start.ox + (move.clientX - start.px),
        y: start.oy + (move.clientY - start.py),
      }
      applyToolbarOffset(toolbar)
    }
    window.addEventListener("pointermove", toolbarMove)
    window.addEventListener("pointerup", end)
  })
}

function setupToolbarDrag(): void {
  const root = holderRef.value
  if (!root) return
  const existing = root.querySelector<HTMLElement>(".ce-inline-toolbar")
  if (existing) {
    attachToolbarGrip(existing)
    return
  }
  toolbarObserver = new MutationObserver(() => {
    const toolbar = root.querySelector<HTMLElement>(".ce-inline-toolbar")
    if (toolbar) {
      attachToolbarGrip(toolbar)
      toolbarObserver?.disconnect()
      toolbarObserver = null
    }
  })
  toolbarObserver.observe(root, { childList: true, subtree: true })
}

function teardownToolbarDrag(): void {
  if (toolbarMove) window.removeEventListener("pointermove", toolbarMove)
  toolbarMove = null
  toolbarObserver?.disconnect()
  toolbarObserver = null
}

let editor: EditorJS | null = null
let editorDefaults: EditorDefaults = makeEmptyDefaults()
let isApplyingEditorData = false
let lastPersistedFormat: string | null = null
let lastAppliedFormat: string | null = null

// --- option sources ------------------------------------------------------

function getForegroundOptions(): ForegroundOption[] {
  const colors = item.datasheet_colors ?? []
  if (!colors.length) {
    return [
      { label: "Black", className: "ce-color-black", color: "#000000" },
      { label: "White", className: "ce-color-white", color: "#ffffff" },
    ]
  }
  return colors.map((color, index) => ({
    label: color,
    className: `ce-color-${index}`,
    color,
  }))
}

function getBackgroundOptions(): BackgroundOption[] {
  const colors = item.datasheet_colors ?? []
  if (!colors.length) return []
  return colors.map((color, index) => ({
    label: color,
    className: `ce-bg-${index}`,
    backgroundColor: color,
  }))
}

function getFontOptions(): FontOption[] {
  const store = useStore()
  const names = new Set<string>()
  const options: FontOption[] = []
  for (const font of store.fonts.urls) {
    if (!font.name || names.has(font.name)) continue
    names.add(font.name)
    options.push({
      label: font.name,
      className: `ce-font-${font.name.replace(/[^a-zA-Z0-9_-]/g, "-")}`,
      fontFamily: font.name,
    })
  }
  if (options.length) return options
  return [
    {
      label: "Sans",
      className: "ce-font-sans",
      fontFamily: "Arial, Helvetica, sans-serif",
    },
    {
      label: "Serif",
      className: "ce-font-serif",
      fontFamily: 'Georgia, "Times New Roman", serif',
    },
    {
      label: "Mono",
      className: "ce-font-mono",
      fontFamily: '"Courier New", Courier, monospace',
    },
  ]
}

function getSizeOptions(): number[] {
  return FONT_SIZE_OPTIONS
}

// --- defaults ------------------------------------------------------------

function readComputed(): EditorDefaults {
  const element = holderRef.value
  const empty = makeEmptyDefaults()
  if (!element) return empty

  const style = window.getComputedStyle(element)
  const usable = (value: string | null | undefined): string | null => {
    if (!value || value === "inherit" || value === "initial") return null
    if (
      value === "transparent" ||
      value === "rgba(0, 0, 0, 0)"
    ) {
      return null
    }
    return value
  }

  const parsedSize = Number.parseFloat(style.fontSize)

  return {
    textColor: usable(style.color),
    foregroundClass: null,
    backgroundColor: usable(style.backgroundColor),
    backgroundClass: null,
    fontFamily: usable(style.fontFamily),
    fontClass: null,
    fontSize:
      Number.isFinite(parsedSize) && parsedSize > 0
        ? parsedSize
        : DEFAULT_FONT_SIZE,
    fontStyle: usable(style.fontStyle),
    fontWeight: usable(style.fontWeight),
  }
}

function getCurrentEditorDefaults(): EditorDefaults {
  return { ...editorDefaults }
}

function applyEditorDefaults(
  defaults: Partial<EditorDefaults>,
  persist = false,
): void {
  editorDefaults = {
    ...editorDefaults,
    ...defaults,
    fontSize:
      defaults.fontSize && Number.isFinite(defaults.fontSize)
        ? defaults.fontSize
        : editorDefaults.fontSize,
  }

  const target = holderRef.value
  if (target) {
    target.style.setProperty(
      "--wdc-default-text-color",
      editorDefaults.textColor || "inherit",
    )
    target.style.setProperty(
      "--wdc-default-background-color",
      editorDefaults.backgroundColor || "transparent",
    )
    target.style.setProperty(
      "--wdc-default-font-family",
      editorDefaults.fontFamily || "inherit",
    )
    target.style.setProperty(
      "--wdc-default-font-size",
      `${editorDefaults.fontSize}pt`,
    )
    target.style.setProperty(
      "--wdc-default-font-style",
      editorDefaults.fontStyle || "normal",
    )
    target.style.setProperty(
      "--wdc-default-font-weight",
      editorDefaults.fontWeight || "normal",
    )
  }

  if (persist && !isApplyingEditorData) {
    void persistEditorData()
  }
}

function parseStoredEditorDefaults(payload: any): Partial<EditorDefaults> | null {
  if (!payload || typeof payload !== "object") return null

  const defaults =
    payload.defaults || extractDefaultsFromBlockTunes(payload.blocks)
  if (!defaults || typeof defaults !== "object") return null

  return {
    textColor:
      typeof defaults.textColor === "string" ? defaults.textColor : null,
    foregroundClass:
      typeof defaults.foregroundClass === "string"
        ? defaults.foregroundClass
        : null,
    backgroundColor:
      typeof defaults.backgroundColor === "string"
        ? defaults.backgroundColor
        : null,
    backgroundClass:
      typeof defaults.backgroundClass === "string"
        ? defaults.backgroundClass
        : null,
    fontFamily:
      typeof defaults.fontFamily === "string" ? defaults.fontFamily : null,
    fontClass:
      typeof defaults.fontClass === "string" ? defaults.fontClass : null,
    fontSize:
      Number.isFinite(defaults.fontSize) && defaults.fontSize > 0
        ? defaults.fontSize
        : editorDefaults.fontSize,
    fontStyle:
      typeof defaults.fontStyle === "string" ? defaults.fontStyle : null,
    fontWeight:
      typeof defaults.fontWeight === "string" ? defaults.fontWeight : null,
  }
}

function extractDefaultsFromBlockTunes(blocks: any): Partial<EditorDefaults> | null {
  if (!Array.isArray(blocks)) return null
  for (const block of blocks) {
    const tuneDefaults = block?.tunes?.editorDefaultsTune
    if (tuneDefaults && typeof tuneDefaults === "object") {
      return tuneDefaults
    }
  }
  return null
}

// --- background-span <-> token round-trip --------------------------------

function transformListItems(
  items: any,
  transform: (value: string) => string,
): any {
  if (!Array.isArray(items)) return items
  return items.map(itemValue => {
    if (typeof itemValue === "string") return transform(itemValue)
    if (Array.isArray(itemValue)) return transformListItems(itemValue, transform)
    if (itemValue && typeof itemValue === "object" && "content" in itemValue) {
      return {
        ...itemValue,
        content:
          typeof itemValue.content === "string"
            ? transform(itemValue.content)
            : itemValue.content,
        items: transformListItems(itemValue.items, transform),
      }
    }
    return itemValue
  })
}

function stripBackgroundSpans(html: string): string {
  const container = document.createElement("div")
  container.innerHTML = html
  const spans = Array.from(container.querySelectorAll("span")).reverse()

  spans.forEach(span => {
    const backgroundClasses = Array.from(span.classList).filter(className =>
      className.startsWith("ce-bg-"),
    )
    if (!backgroundClasses.length) return

    const serializedClassName = backgroundClasses.join(" ")
    const openToken = document.createTextNode(
      `${BG_TOKEN_OPEN}${serializedClassName}]]`,
    )
    const closeToken = document.createTextNode(BG_TOKEN_CLOSE)
    const parent = span.parentNode
    if (!parent) return

    parent.insertBefore(openToken, span)
    while (span.firstChild) {
      parent.insertBefore(span.firstChild, span)
    }
    parent.insertBefore(closeToken, span)
    parent.removeChild(span)
  })

  return container.innerHTML
}

function restoreBackgroundSpans(html: string): string {
  let restored = html
  let previous = ""
  while (restored !== previous) {
    previous = restored
    restored = restored.replace(
      /\[\[WDC_BG:([^\]]+)\]\]([\s\S]*?)\[\[\/WDC_BG\]\]/g,
      (_match, className, content) => `<span class="${className}">${content}</span>`,
    )
  }
  return restored
}

function serializeBlockData(type: string, data: any): any {
  if (!data) return data
  switch (type) {
    case "header":
    case "paragraph":
      return {
        ...data,
        text:
          typeof data.text === "string"
            ? stripBackgroundSpans(data.text)
            : data.text,
      }
    case "table":
      return {
        ...data,
        content: Array.isArray(data.content)
          ? data.content.map((row: any) =>
              Array.isArray(row)
                ? row.map((cell: any) =>
                    typeof cell === "string" ? stripBackgroundSpans(cell) : cell,
                  )
                : row,
            )
          : data.content,
      }
    case "list":
      return {
        ...data,
        items: transformListItems(data.items, value =>
          stripBackgroundSpans(value),
        ),
      }
    default:
      return data
  }
}

function hydrateBlockData(type: string, data: any): any {
  if (!data) return data
  switch (type) {
    case "header":
    case "paragraph":
      return {
        ...data,
        text:
          typeof data.text === "string"
            ? restoreBackgroundSpans(data.text)
            : data.text,
      }
    case "table":
      return {
        ...data,
        content: Array.isArray(data.content)
          ? data.content.map((row: any) =>
              Array.isArray(row)
                ? row.map((cell: any) =>
                    typeof cell === "string" ? restoreBackgroundSpans(cell) : cell,
                  )
                : row,
            )
          : data.content,
      }
    case "list":
      return {
        ...data,
        items: transformListItems(data.items, value =>
          restoreBackgroundSpans(value),
        ),
      }
    default:
      return data
  }
}

function serializeBackgroundMarkup(outputData: OutputData): OutputData {
  return {
    ...outputData,
    blocks: outputData.blocks.map(block => ({
      ...block,
      data: serializeBlockData(block.type, block.data),
    })),
  }
}

function hydrateBackgroundMarkup(outputData: OutputData): OutputData {
  return {
    ...outputData,
    blocks: outputData.blocks.map(block => ({
      ...block,
      data: hydrateBlockData(block.type, block.data),
    })),
  }
}

// --- base64 helpers ------------------------------------------------------

function bytesToBase64(bytes: Uint8Array): string {
  let binary = ""
  const chunk = 0x8000
  for (let offset = 0; offset < bytes.length; offset += chunk) {
    binary += String.fromCharCode(...bytes.subarray(offset, offset + chunk))
  }
  return btoa(binary)
}

function base64ToBytes(base64: string): Uint8Array {
  return Uint8Array.from(atob(base64), c => c.charCodeAt(0))
}

/* see docs/EXTRACTED_COMMENTS.md#c340 */
function resolveFormat(): string | null {
  const src = item.published.src
  return typeof src === "string" && src.length > 0 ? src : null
}

// --- persistence ---------------------------------------------------------

async function persistEditorData(): Promise<void> {
  if (outputOnly || isApplyingEditorData || !editor) return

  try {
    const outputData = await editor.save()
    const serialized = {
      ...serializeBackgroundMarkup(outputData),
      defaults: getCurrentEditorDefaults(),
    }
    const buf = strToU8(JSON.stringify(serialized))
    const compressed = zlibSync(buf, { level: 6, mem: 8 })
    const base64Data = bytesToBase64(compressed)

    if (base64Data === lastPersistedFormat) return
    lastPersistedFormat = base64Data
    // see docs/EXTRACTED_COMMENTS.md#c341
    lastAppliedFormat = base64Data

    useStore().socketManager.sendTextUpdate(item, base64Data);
  } catch (error) {
    console.log("Saving failed: ", error)
  }
}

async function applyFormat(): Promise<void> {
  if (!editor) return
  const format = resolveFormat()

  if (!format) {
    lastAppliedFormat = null
    return
  }
  if (format === lastAppliedFormat) {
    lastPersistedFormat = format
    return
  }

  await editor.isReady
  try {

    const decompressed = strFromU8(decompressSync(base64ToBytes(format)))
    if (!decompressed) return

    const parsedPayload = JSON.parse(decompressed)
    const storedDefaults = parseStoredEditorDefaults(parsedPayload)
    const hydrated = hydrateBackgroundMarkup(parsedPayload)

    isApplyingEditorData = true
    try {
      if (storedDefaults) applyEditorDefaults(storedDefaults, false)
      await editor.blocks.render({ blocks: hydrated.blocks })
      lastAppliedFormat = format
      lastPersistedFormat = format
    } finally {
      window.setTimeout(() => {
        isApplyingEditorData = false
      }, 0)
    }
  } catch (error) {
    console.log("Applying editor format failed: ", error)
  }
}

// --- styling -------------------------------------------------------------

function buildScopedStyle(): string {
  const classRules = [
    ...getForegroundOptions().map(
      option =>
        `#${holderId} .${option.className}{color:${toRgb(option.color) ?? option.color};}`,
    ),
    ...getBackgroundOptions().map(
      option =>
        `#${holderId} .${option.className}{background-color:${toRgb(option.backgroundColor) ?? option.backgroundColor};}`,
    ),
    ...getFontOptions().map(
      option => `#${holderId} .${option.className}{font-family:${option.fontFamily};}`,
    ),
  ].join("\n")

  return `
    /* ---- design tokens (inherited by every editor surface) ---- */
    #${holderId} {
      --wdc-ink: #0f172a;
      --wdc-ink-soft: #475569;
      --wdc-muted: #94a3b8;
      --wdc-line: rgba(15, 23, 42, 0.08);
      --wdc-line-2: rgba(15, 23, 42, 0.14);
      --wdc-fill: #f1f5f9;
      --wdc-fill-hover: #e7ebf1;
      --wdc-panel: #ffffff;
      /* see docs/EXTRACTED_COMMENTS.md#c041 */
      --wdc-accent: #3f3f46;
      --wdc-accent-soft: rgba(63, 63, 70, 0.09);
      /* see docs/EXTRACTED_COMMENTS.md#c042 */
      --wdc-success: #15803d;
      --wdc-success-border: #22c55e;
      --wdc-success-soft: rgba(34, 197, 94, 0.14);
      /* Blue focus ring for cells being edited. */
      --wdc-focus: #2563eb;
      --wdc-r-lg: 14px;
      --wdc-r-md: 9px;
      --wdc-r-sm: 6px;
      --wdc-shadow: 0 14px 36px -14px rgba(15, 23, 42, 0.30), 0 2px 8px -3px rgba(15, 23, 42, 0.12);
      --wdc-ease: 150ms cubic-bezier(0.2, 0.7, 0.3, 1);
    }

    /* ---- content surface ---- */
    #${holderId} .codex-editor { --content-width: 100%; }
    #${holderId} .codex-editor__redactor { padding-top: 0 !important; }
    #${holderId} .codex-editor__redactor,
    #${holderId} .ce-block__content,
    #${holderId} .ce-toolbar__content { max-width: 100% !important; }
    #${holderId} .ce-paragraph,
    #${holderId} .ce-header,
    #${holderId} .cdx-input,
    #${holderId} .tc-table__area,
    #${holderId} .tc-table__inp,
    #${holderId} .tc-table__inp * {
      color: var(--wdc-default-text-color, inherit);
      background-color: var(--wdc-default-background-color, transparent);
      font-family: var(--wdc-default-font-family, inherit);
      font-size: var(--wdc-default-font-size, ${DEFAULT_FONT_SIZE}pt);
      font-style: var(--wdc-default-font-style, normal);
      font-weight: var(--wdc-default-font-weight, normal);
    }
    #${holderId} .ce-block:first-of-type,
    #${holderId} .cdx-block:first-of-type {
      margin-top: 0 !important;
      padding-top: 0 !important;
    }
    #${holderId} ::selection { background: var(--wdc-accent-soft); }
    #${holderId} .codex-editor--empty .ce-paragraph[data-placeholder]:empty::before {
      color: var(--wdc-muted);
      font-style: normal;
    }

    /* ---- EditorJS chrome: gutter buttons ---- */
    #${holderId} .ce-toolbar__plus,
    #${holderId} .ce-toolbar__settings-btn {
      color: var(--wdc-ink-soft);
      border-radius: var(--wdc-r-sm);
      border: 1px solid transparent;
      transition: background var(--wdc-ease), color var(--wdc-ease), border-color var(--wdc-ease);
    }
    #${holderId} .ce-toolbar__plus:hover,
    #${holderId} .ce-toolbar__settings-btn:hover {
      background: var(--wdc-fill);
      color: var(--wdc-ink);
      border-color: var(--wdc-line);
    }

    /* ---- EditorJS chrome: popovers & inline toolbar ---- */
    #${holderId} .ce-popover,
    #${holderId} .ce-popover--opened > .ce-popover__container,
    #${holderId} .ce-inline-toolbar,
    #${holderId} .ce-conversion-toolbar {
      border: 1px solid var(--wdc-line);
      border-radius: var(--wdc-r-lg);
      box-shadow: var(--wdc-shadow);
      background: var(--wdc-panel);
      backdrop-filter: saturate(1.4) blur(6px);
    }
    #${holderId} .ce-popover-item,
    #${holderId} .ce-conversion-tool {
      border-radius: var(--wdc-r-md);
      color: var(--wdc-ink);
      transition: background var(--wdc-ease);
    }
    #${holderId} .ce-popover-item:hover,
    #${holderId} .ce-conversion-tool:hover { background: var(--wdc-fill) !important; }
    #${holderId} .ce-popover-item--active {
      background: var(--wdc-accent-soft) !important;
      color: var(--wdc-accent);
    }
    #${holderId} .ce-inline-toolbar { padding: 4px; }
    #${holderId} .ce-inline-tool,
    #${holderId} .ce-inline-toolbar__dropdown {
      color: var(--wdc-ink);
      border-radius: var(--wdc-r-sm);
      transition: background var(--wdc-ease), color var(--wdc-ease);
    }
    #${holderId} .ce-inline-tool:hover,
    #${holderId} .ce-inline-toolbar__dropdown:hover { background: var(--wdc-fill); }
    #${holderId} .ce-inline-tool--active { color: var(--wdc-accent); background: var(--wdc-accent-soft); }
    /* drag grip prepended to the inline toolbar */
    #${holderId} .wdc-tb-grip {
      display: inline-grid; place-items: center; align-self: stretch;
      width: 16px; padding: 0; margin-right: 4px;
      border: 0; border-right: 1px solid var(--wdc-line); background: transparent;
      color: var(--wdc-muted); cursor: grab; touch-action: none;
      transition: color var(--wdc-ease);
    }
    #${holderId} .wdc-tb-grip:hover { color: var(--wdc-ink); }
    #${holderId} .wdc-tb-grip:active { cursor: grabbing; }
    #${holderId} .wdc-tb-grip svg { fill: currentColor; }

    /* ---- shared popover shell (our picker tools) ---- */
    #${holderId} .wdc-pop {
      padding: 8px;
      min-width: 184px;
      max-height: 264px;
      overflow-y: auto;
      animation: wdc-pop-in 140ms cubic-bezier(0.2, 0.7, 0.3, 1);
    }
    @keyframes wdc-pop-in {
      from { opacity: 0; transform: translateY(-4px); }
      to { opacity: 1; transform: none; }
    }
    #${holderId} .wdc-pop__body { display: block; }
    #${holderId} .wdc-pop--swatch .wdc-pop__body {
      display: grid; grid-template-columns: repeat(6, 1fr); gap: 6px;
    }
    #${holderId} .wdc-pop--chips .wdc-pop__body {
      display: flex; flex-wrap: wrap; gap: 6px;
    }
    #${holderId} .wdc-pop--list .wdc-pop__body { display: flex; flex-direction: column; gap: 2px; }

    /* ---- custom colour control ---- */
    #${holderId} .wdc-custom {
      display: flex; align-items: center; gap: 8px;
      padding: 6px; margin-bottom: 8px;
      border-radius: var(--wdc-r-md);
      background: var(--wdc-fill);
    }
    #${holderId} .wdc-custom__chip {
      position: relative; width: 28px; height: 28px; flex: none;
      border-radius: 50%; cursor: pointer;
      background: var(--swatch, #1f2937);
      box-shadow: inset 0 0 0 1px var(--wdc-line-2), 0 1px 2px rgba(15, 23, 42, 0.15);
      transition: transform var(--wdc-ease);
    }
    #${holderId} .wdc-custom__chip:hover { transform: scale(1.06); }
    #${holderId} .wdc-custom__native {
      position: absolute; inset: 0; width: 100%; height: 100%;
      opacity: 0; border: 0; padding: 0; cursor: pointer;
    }
    #${holderId} .wdc-custom__hex {
      flex: 1; min-width: 0; width: 100%;
      padding: 6px 8px; border: 1px solid var(--wdc-line); border-radius: var(--wdc-r-sm);
      background: var(--wdc-panel); color: var(--wdc-ink);
      font-size: 12px; font-variant-numeric: tabular-nums; letter-spacing: 0.04em;
      text-transform: uppercase;
      transition: border-color var(--wdc-ease), box-shadow var(--wdc-ease);
    }
    #${holderId} .wdc-custom__hex:focus {
      outline: none; border-color: var(--wdc-accent);
      box-shadow: 0 0 0 3px var(--wdc-accent-soft);
    }
    #${holderId} .wdc-custom__eye {
      display: grid; place-items: center; width: 28px; height: 28px; flex: none;
      border: 1px solid var(--wdc-line); border-radius: var(--wdc-r-sm);
      background: var(--wdc-panel); color: var(--wdc-ink-soft); cursor: pointer;
      transition: background var(--wdc-ease), color var(--wdc-ease), border-color var(--wdc-ease);
    }
    #${holderId} .wdc-custom__eye:hover {
      background: var(--wdc-accent-soft); color: var(--wdc-accent); border-color: transparent;
    }

    /* ---- swatch grid ---- */
    #${holderId} .wdc-swatch {
      width: 100%; aspect-ratio: 1 / 1; padding: 0;
      border: 0; border-radius: var(--wdc-r-sm); cursor: pointer;
      background: var(--swatch, transparent);
      box-shadow: inset 0 0 0 1px var(--wdc-line-2);
      transition: transform var(--wdc-ease), box-shadow var(--wdc-ease);
    }
    #${holderId} .wdc-swatch:hover {
      transform: translateY(-1px) scale(1.06);
      box-shadow: inset 0 0 0 1px var(--wdc-line-2), 0 0 0 2px var(--wdc-accent-soft);
    }
    /* "remove colour" chip — white with a red diagonal slash */
    #${holderId} .wdc-swatch--clear {
      background:
        linear-gradient(to top left, transparent calc(50% - 1px), #ef4444 calc(50% - 1px),
          #ef4444 calc(50% + 1px), transparent calc(50% + 1px)),
        var(--wdc-panel);
    }

    /* ---- list rows (fonts) ---- */
    #${holderId} .wdc-row {
      display: flex; align-items: center; gap: 12px;
      width: 100%; padding: 8px 10px; border: 0; cursor: pointer;
      background: transparent; border-radius: var(--wdc-r-md); text-align: left;
      transition: background var(--wdc-ease);
    }
    #${holderId} .wdc-row:hover { background: var(--wdc-fill); }
    #${holderId} .wdc-row__preview {
      font-size: 17px; line-height: 1; color: var(--wdc-ink);
      width: 30px; text-align: center; flex: none;
    }
    #${holderId} .wdc-row__label { font-size: 13px; color: var(--wdc-ink-soft); }

    /* ---- size chips ---- */
    #${holderId} .wdc-chip {
      min-width: 38px; padding: 7px 10px; border: 1px solid var(--wdc-line);
      border-radius: 999px; cursor: pointer; background: var(--wdc-panel);
      font-size: 13px; font-variant-numeric: tabular-nums; color: var(--wdc-ink);
      transition: background var(--wdc-ease), border-color var(--wdc-ease), color var(--wdc-ease);
    }
    #${holderId} .wdc-chip:hover {
      background: var(--wdc-accent-soft); border-color: transparent; color: var(--wdc-accent);
    }

    /* ---- block defaults tune ---- */
    .wdc-tune {
      display: flex; flex-direction: column; gap: 12px;
      padding: 12px; min-width: 224px;
      font: 13px/1.4 var(--wdc-default-font-family, inherit);
      color: var(--wdc-ink, #0f172a);
    }
    .wdc-tune__head {
      font-size: 11px; font-weight: 700; letter-spacing: 0.09em;
      text-transform: uppercase; color: var(--wdc-muted, #94a3b8);
    }
    .wdc-tune__split { display: grid; grid-template-columns: 1fr auto; gap: 12px; }
    .wdc-tune__field { display: flex; flex-direction: column; gap: 6px; }
    .wdc-tune__label {
      font-size: 11px; font-weight: 600; letter-spacing: 0.02em;
      color: var(--wdc-ink-soft, #475569);
    }
    .wdc-tune__colors { display: flex; flex-wrap: wrap; align-items: center; gap: 7px; }
    .wdc-tune__custom {
      position: relative; width: 26px; height: 26px; flex: none;
      border-radius: 50%; cursor: pointer; background: var(--swatch, #1f2937);
      box-shadow: inset 0 0 0 1px var(--wdc-line-2, rgba(15,23,42,0.14)), 0 1px 2px rgba(15,23,42,0.15);
    }
    .wdc-tune__custom::after {
      content: "+"; position: absolute; inset: 0; display: grid; place-items: center;
      color: #fff; font-size: 15px; font-weight: 700; mix-blend-mode: difference;
    }
    .wdc-tune__custom input { position: absolute; inset: 0; opacity: 0; cursor: pointer; }
    .wdc-tune__swatch {
      width: 22px; height: 22px; padding: 0; border: 0; border-radius: var(--wdc-r-sm, 6px);
      cursor: pointer; background: var(--swatch, transparent);
      box-shadow: inset 0 0 0 1px var(--wdc-line-2, rgba(15,23,42,0.14));
      transition: transform 150ms ease;
    }
    .wdc-tune__swatch:hover { transform: scale(1.1); }
    .wdc-tune__swatch--clear {
      background:
        linear-gradient(to top left, transparent calc(50% - 1px), #ef4444 calc(50% - 1px),
          #ef4444 calc(50% + 1px), transparent calc(50% + 1px)),
        var(--wdc-panel, #fff);
    }
    .wdc-tune__select {
      width: 100%; padding: 7px 9px; border-radius: var(--wdc-r-md, 9px);
      border: 1px solid var(--wdc-line, rgba(15,23,42,0.08)); background: var(--wdc-panel, #fff);
      color: var(--wdc-ink, #0f172a); font-size: 13px; cursor: pointer;
      transition: border-color 150ms ease, box-shadow 150ms ease;
    }
    .wdc-tune__select:focus {
      outline: none; border-color: var(--wdc-accent, #2563eb);
      box-shadow: 0 0 0 3px var(--wdc-accent-soft, rgba(37,99,235,0.12));
    }
    .wdc-tune__segment { display: inline-flex; gap: 4px; }
    .wdc-tune__seg {
      width: 34px; height: 34px; border-radius: var(--wdc-r-md, 9px);
      border: 1px solid var(--wdc-line, rgba(15,23,42,0.08)); background: var(--wdc-panel, #fff);
      color: var(--wdc-ink-soft, #475569); font-size: 14px; cursor: pointer;
      transition: background 150ms ease, color 150ms ease, border-color 150ms ease;
    }
    .wdc-tune__seg:hover { background: var(--wdc-fill, #f1f5f9); }
    .wdc-tune__seg.is-active {
      background: var(--wdc-accent, #2563eb); border-color: var(--wdc-accent, #2563eb); color: #fff;
    }

    /* ---- spreadsheet table ---- */
    /* see docs/EXTRACTED_COMMENTS.md#c043 */
    #${holderId} .wdc-grid {
      position: relative; margin: 6px 0; max-width: 100%;
      display: grid;
      grid-template-columns: minmax(0, max-content) 24px;
      grid-template-rows: auto 24px;
    }
    #${holderId} .wdc-grid__main {
      grid-column: 1; grid-row: 1;
      display: flex; align-items: flex-start;
      max-width: 100%; overflow-x: auto;
      /* see docs/EXTRACTED_COMMENTS.md#c044 */
      overflow-y: auto;
    }
    #${holderId} .wdc-grid__table {
      flex: 0 1 auto;
      display: grid;
      /* see docs/EXTRACTED_COMMENTS.md#c045 */
      grid-template-columns: 26px repeat(var(--cols), minmax(64px, max-content));
      border: 1px solid var(--wdc-line-2);
      border-radius: var(--wdc-r-md) 0 0 var(--wdc-r-md);
      overflow: hidden;
    }
    /* slim left gutter (corner + row numbers) */
    #${holderId} .wdc-grid__corner,
    #${holderId} .wdc-grid__gutter {
      background: var(--wdc-fill);
      border-right: 1px solid var(--wdc-line-2);
      border-bottom: 1px solid var(--wdc-line);
    }
    #${holderId} .wdc-grid__gutter {
      position: relative;
      display: flex; align-items: center; justify-content: center;
      min-height: 26px; padding: 0 2px;
      border-top: 0; border-left: 0;
      color: var(--wdc-muted); font-size: 10px; font-variant-numeric: tabular-nums;
      cursor: pointer; user-select: none;
      transition: background var(--wdc-ease), color var(--wdc-ease);
    }
    #${holderId} .wdc-grid__gutter:hover { background: var(--wdc-accent-soft); color: var(--wdc-accent); }
    #${holderId} .wdc-grid__gutter:disabled { cursor: default; }
    /* see docs/EXTRACTED_COMMENTS.md#c046 */
    #${holderId} .wdc-grid__gutternum { transition: opacity var(--wdc-ease); }
    #${holderId} .wdc-grid__gutter:hover .wdc-grid__gutternum { opacity: 0; }
    #${holderId} .wdc-grid__guttercaret {
      position: absolute; inset: 0; display: grid; place-items: center;
      opacity: 0; color: var(--wdc-accent); transition: opacity var(--wdc-ease);
    }
    #${holderId} .wdc-grid__gutter:hover .wdc-grid__guttercaret { opacity: 1; }
    #${holderId} .wdc-grid__guttercaret svg { width: 13px; height: 13px; }
    /* header row (first row of content) */
    #${holderId} .wdc-grid__head {
      position: relative; display: flex; align-items: stretch;
      background: var(--wdc-fill);
      border-right: 1px solid var(--wdc-line);
      border-bottom: 1px solid var(--wdc-line-2);
    }
    #${holderId} .wdc-grid__headtext {
      flex: 1; min-width: 0; min-height: 28px;
      padding: 6px 22px 6px 9px; outline: none;
      color: var(--wdc-ink); font-weight: 600;
      white-space: normal; overflow-wrap: anywhere;
    }
    #${holderId} .wdc-grid__headtext:empty::before {
      content: attr(data-placeholder); color: var(--wdc-muted); opacity: 0.7;
      font-weight: 600; pointer-events: none;
    }
    #${holderId} .wdc-grid__headtext:focus { box-shadow: inset 0 0 0 2px var(--wdc-focus); }
    #${holderId} .wdc-grid__headmenu {
      position: absolute; top: 50%; right: 3px; transform: translateY(-50%);
      display: grid; place-items: center; width: 18px; height: 18px;
      padding: 0; border: 0; border-radius: var(--wdc-r-sm);
      background: transparent; color: var(--wdc-accent); cursor: pointer;
      opacity: 0; transition: opacity var(--wdc-ease), background var(--wdc-ease), color var(--wdc-ease);
    }
    #${holderId} .wdc-grid__head:hover .wdc-grid__headmenu { opacity: 1; }
    #${holderId} .wdc-grid__headmenu:hover { background: var(--wdc-accent-soft); color: var(--wdc-accent); }
    #${holderId} .wdc-grid__headmenu svg { width: 13px; height: 13px; }
    /* data cells */
    #${holderId} .wdc-grid__cell {
      min-height: 26px; padding: 7px 9px; outline: none;
      border-right: 1px solid var(--wdc-line);
      border-bottom: 1px solid var(--wdc-line);
      color: var(--wdc-default-text-color, inherit);
      white-space: normal; overflow-wrap: anywhere;
    }
    #${holderId} .wdc-grid__cell:focus { box-shadow: inset 0 0 0 2px var(--wdc-focus); }
    /* see docs/EXTRACTED_COMMENTS.md#c047 */
    #${holderId} .wdc-grid__addcol,
    #${holderId} .wdc-grid__addrow {
      box-sizing: border-box;
      display: grid; place-items: center;
      padding: 0; cursor: pointer;
      border: 1.5px dashed transparent; border-radius: var(--wdc-r-sm);
      background: transparent; color: var(--wdc-muted);
      transition: background 120ms ease, border-color 120ms ease, color 120ms ease;
    }
    #${holderId} .wdc-grid__addcol { grid-column: 2; grid-row: 1; }
    #${holderId} .wdc-grid__addrow { grid-column: 1; grid-row: 2; }
    #${holderId} .wdc-grid__addcol:hover,
    #${holderId} .wdc-grid__addrow:hover {
      background: var(--wdc-success-soft);
      border-color: var(--wdc-success-border);
      color: var(--wdc-success);
    }
    #${holderId} .wdc-grid__addcol:focus-visible,
    #${holderId} .wdc-grid__addrow:focus-visible {
      outline: none;
      background: var(--wdc-success-soft);
      border-color: var(--wdc-success-border);
      color: var(--wdc-success);
    }
    #${holderId} .wdc-grid__addrow svg,
    #${holderId} .wdc-grid__addcol svg {
      width: 16px; height: 16px;
      transition: transform 120ms ease;
    }
    #${holderId} .wdc-grid__addcol:hover svg,
    #${holderId} .wdc-grid__addrow:hover svg,
    #${holderId} .wdc-grid__addcol:focus-visible svg,
    #${holderId} .wdc-grid__addrow:focus-visible svg { transform: scale(1.35); }
    #${holderId} .wdc-grid__menu {
      position: absolute; z-index: 6; min-width: 168px; padding: 6px;
      display: flex; flex-direction: column; gap: 1px;
      background: var(--wdc-panel); border: 1px solid var(--wdc-line);
      border-radius: var(--wdc-r-md); box-shadow: var(--wdc-shadow);
      animation: wdc-pop-in 140ms cubic-bezier(0.2, 0.7, 0.3, 1);
    }
    #${holderId} .wdc-grid__menuitem {
      display: flex; align-items: center; gap: 9px;
      padding: 7px 10px 7px 8px; border: 0; border-radius: var(--wdc-r-sm);
      background: transparent; color: var(--wdc-ink);
      font-size: 13px; text-align: left; cursor: pointer;
      transition: background var(--wdc-ease);
    }
    #${holderId} .wdc-grid__menuicon {
      display: grid; place-items: center; width: 16px; height: 16px;
      flex: none; color: var(--wdc-muted);
    }
    #${holderId} .wdc-grid__menuicon svg { width: 15px; height: 15px; }
    #${holderId} .wdc-grid__menulabel { flex: 1; }
    #${holderId} .wdc-grid__menuitem:hover { background: var(--wdc-fill); }
    #${holderId} .wdc-grid__menuitem:hover .wdc-grid__menuicon { color: var(--wdc-ink-soft); }
    #${holderId} .wdc-grid__menuitem.is-danger,
    #${holderId} .wdc-grid__menuitem.is-danger .wdc-grid__menuicon { color: #dc2626; }
    #${holderId} .wdc-grid__menuitem.is-danger:hover { background: rgba(220, 38, 38, 0.1); }
    #${holderId} .wdc-grid__menuitem.is-disabled,
    #${holderId} .wdc-grid__menuitem.is-disabled .wdc-grid__menuicon {
      color: var(--wdc-muted); cursor: default; opacity: 0.55;
    }
    #${holderId} .wdc-grid__menuitem.is-disabled:hover { background: transparent; }
    #${holderId} .wdc-grid__menusep {
      height: 1px; margin: 4px 6px; background: var(--wdc-line);
    }

    ${classRules}
  `
}

// --- lifecycle -----------------------------------------------------------

onMounted(async () => {
  if (!holderRef.value) return

  editorDefaults = readComputed()

  const styleEl = document.createElement("style")
  styleEl.dataset.editorStyle = holderId
  styleEl.textContent = buildScopedStyle()
  document.head.appendChild(styleEl)
  styleRef.value = styleEl

  applyEditorDefaults(editorDefaults, false)

  const store = useStore();
  if (!outputOnly) store.socketManager.requestButtonStatus(item);

  // see docs/EXTRACTED_COMMENTS.md#c342
  const [
    { default: EditorJSCtor },
    { default: Header },
    { default: Paragraph },
    { default: List },
    { default: AlignmentTuneTool },
  ] = await Promise.all([
    import("@editorjs/editorjs"),
    import("@editorjs/header"),
    import("@editorjs/paragraph"),
    import("@editorjs/list"),
    import("editorjs-text-alignment-blocktune"),
  ])

  // see docs/EXTRACTED_COMMENTS.md#c343
  if (!holderRef.value) return

  const foregroundOptions = getForegroundOptions()
  const backgroundOptions = getBackgroundOptions()
  const fontOptions = getFontOptions()
  const fontSizeOptions = getSizeOptions()

  editor = new EditorJSCtor({
    holder: holderRef.value,
    readOnly,
    autofocus: false,
    placeholder: "Enter your text here...",
    minHeight: 0,
    tools: {
      header: {
        class: Header as any,
        inlineToolbar: true,
        shortcut: "CTRL+SHIFT+H",
        tunes: ["anyTuneName", "editorDefaultsTune"],
        config: { levels: [1, 2], defaultLevel: 1 },
      },
      paragraph: {
        class: Paragraph as any,
        inlineToolbar: true,
        shortcut: "CTRL+SHIFT+X",
        tunes: ["anyTuneName", "editorDefaultsTune"],
      },
      table: {
        class: SpreadsheetTableTool as any,
        inlineToolbar: true,
        tunes: ["anyTuneName", "editorDefaultsTune"],
        // see docs/EXTRACTED_COMMENTS.md#c344
        config: {
          onChange: () => {
            if (isApplyingEditorData) return
            void persistEditorData()
          },
        },
      },
      list: {
        class: List as any,
        inlineToolbar: true,
        tunes: ["anyTuneName", "editorDefaultsTune"],
      },
      anyTuneName: {
        class: AlignmentTuneTool as any,
        config: {
          default: "left",
          blocks: { header: "center", list: "left" },
        },
      },
      editorDefaultsTune: {
        class: EditorDefaultsTune as any,
        config: {
          getDefaults: () => getCurrentEditorDefaults(),
          applyDefaults: (defaults: Partial<EditorDefaults>) =>
            applyEditorDefaults(defaults, true),
          getForegroundOptions,
          getBackgroundOptions,
          getFontOptions,
          getSizeOptions,
        },
      },
      colorPicker: {
        class: ForegroundPickerTool as any,
        config: { colors: foregroundOptions },
      },
      backgroundPicker: {
        class: BackgroundPickerTool as any,
        config: { backgrounds: backgroundOptions },
      },
      fontPicker: {
        class: FontPickerTool as any,
        config: { fonts: fontOptions },
      },
      fontSize: {
        class: FontSizeTool as any,
        config: { sizes: fontSizeOptions },
      },
    },
    inlineToolbar: [
      "bold",
      "italic",
      "colorPicker",
      "backgroundPicker",
      "fontPicker",
      "fontSize",
    ],
    data: { blocks: [] },
    onChange: () => {
      if (isApplyingEditorData) return
      void persistEditorData()
    },
  })

  void editor.isReady.then(() => {
    applyFormat()
    if (!outputOnly) setupToolbarDrag()
    setupGridResize()
  })
})

// see docs/EXTRACTED_COMMENTS.md#c345
watch(
  () => resolveFormat(),
  () => void applyFormat(),
)

onBeforeUnmount(() => {
  teardownToolbarDrag()
  teardownGridResize()
  if (editor && typeof (editor as any).destroy === "function") {
    ;(editor as any).destroy()
  }
  editor = null
  if (styleRef.value?.parentNode) {
    styleRef.value.parentNode.removeChild(styleRef.value)
  }
  styleRef.value = null
})
</script>

<template>
  <div v-bind="item.props" ref="boxRef">
    <div :style="canvasStyle">
      <div
        :id="holderId"
        ref="holderRef"
        :style="{ position: 'absolute', top: 0, left: 0, width: '100%', overflow: 'visible' }"
      ></div>
    </div>
    <slot></slot>
  </div>
</template>

<style scoped>
</style>
